/**
 * The topbar's Live / Test / All switch. Lists and the dashboard read it
 * instead of having their own environment filter. It starts on Live and
 * is remembered per browser, so the switch must make Test obvious.
 */
import { createContext, useContext, useState, type ReactNode } from "react";
import type { TenantEnvironment } from "@/lib/api";

const STORAGE_KEY = "senti-admin-environment";
const ALL = "all";

interface EnvironmentContextValue {
  /** ``undefined`` means all environments. */
  environment: TenantEnvironment | undefined;
  setEnvironment: (next: TenantEnvironment | undefined) => void;
}

const EnvironmentContext = createContext<EnvironmentContextValue>({
  environment: undefined,
  setEnvironment: () => {},
});

function readStored(): TenantEnvironment | undefined {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === ALL) return undefined;
    return stored === "test" ? "test" : "live";
  } catch {
    return "live";
  }
}

export function EnvironmentProvider({ children }: { children: ReactNode }) {
  const [environment, setState] = useState(readStored);
  function setEnvironment(next: TenantEnvironment | undefined) {
    setState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ?? ALL);
    } catch {
      // Private window or blocked storage: the switch still works until reload.
    }
  }
  return (
    <EnvironmentContext.Provider value={{ environment, setEnvironment }}>
      {children}
    </EnvironmentContext.Provider>
  );
}

export function useEnvironment() {
  return useContext(EnvironmentContext);
}
